import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setUser, setAuthenticated, setLoading } from '../../core/store/slices/userSlice';
import { getApiBaseUrl } from '../../core/config/api';
import { User } from '../types';

/**
 * Composant qui restaure la session utilisateur au chargement de l'application
 * Vérifie si un token existe dans localStorage et restaure l'utilisateur si la session est valide
 */
const SessionRestorer: React.FC = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    let retryCount = 0;
    const MAX_RETRIES = 3;
    let isMounted = true;

    const restoreSession = async (): Promise<void> => {
      // Vérifier si le composant est toujours monté
      if (!isMounted) return;

      // Récupérer le token depuis localStorage
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      
      if (!token) {
        // Pas de token, l'utilisateur n'est pas connecté
        if (isMounted) {
          dispatch(setLoading(false));
        }
        return;
      }

      try {
        // Vérifier la session avec le backend
        // getApiBaseUrl(true) retourne déjà l'URL avec /api, donc on ajoute seulement /auth/profile
        const response = await fetch(`${getApiBaseUrl(true)}/auth/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!isMounted) return;

        if (response.ok) {
          const data = await response.json();
          
          if (data.success && data.user) {
            // Adapter l'utilisateur au format attendu par Redux
            const userForRedux: User = {
              ...data.user,
              activated: data.user.activated !== undefined ? data.user.activated : true,
              bann: 0,
              premium: false,
              roles_id: data.user.roles_id || 6, // Utiliser le roles_id de l'API ou 6 (Joueur) par défaut
            };

            // Restaurer l'utilisateur dans Redux
            if (isMounted) {
              dispatch(setUser(userForRedux));
              dispatch(setAuthenticated(true));
              dispatch(setLoading(false));
              console.log('✅ Session restaurée:', userForRedux.username);
            }
          } else {
            // Session invalide, supprimer le token
            if (isMounted) {
              localStorage.removeItem('token');
              localStorage.removeItem('authToken');
              dispatch(setUser(null));
              dispatch(setAuthenticated(false));
              dispatch(setLoading(false));
            }
          }
        } else if (response.status === 401 || response.status === 403 || response.status === 503) {
          // Session invalide, refus, ou maintenance (503) : déconnexion locale
          if (isMounted) {
            localStorage.removeItem('token');
            localStorage.removeItem('authToken');
            dispatch(setUser(null));
            dispatch(setAuthenticated(false));
            dispatch(setLoading(false));
          }
        } else {
          // Autre erreur HTTP (500, 503, etc.), retenter si on n'a pas dépassé le maximum
          retryCount++;
          if (retryCount < MAX_RETRIES && isMounted) {
            // Ne logger qu'en mode développement pour réduire le bruit
            if (process.env.NODE_ENV === 'development') {
              console.warn(`Erreur serveur lors de la restauration de session (tentative ${retryCount}/${MAX_RETRIES}), nouvelle tentative dans 2 secondes...`, response.status);
            }
            setTimeout(() => {
              if (isMounted) {
                restoreSession();
              }
            }, 2000);
          } else if (isMounted) {
            // Trop de tentatives, garder le token mais arrêter les tentatives
            if (process.env.NODE_ENV === 'development') {
              console.error('Impossible de restaurer la session après plusieurs tentatives');
            }
            dispatch(setLoading(false));
          }
        }
      } catch (error: any) {
        if (!isMounted) return;

        // Vérifier si c'est une erreur de connexion (serveur non disponible)
        const isConnectionError = error.message?.includes('Failed to fetch') || 
                                  error.message?.includes('ERR_CONNECTION_REFUSED') ||
                                  error.name === 'TypeError';

        // Pour les erreurs de connexion, on arrête après quelques tentatives
        // car cela indique que le serveur n'est probablement pas démarré
        if (isConnectionError) {
          retryCount++;
          if (retryCount < MAX_RETRIES) {
            // Ne logger qu'en mode développement pour réduire le bruit
            if (process.env.NODE_ENV === 'development') {
              console.warn(`Serveur non disponible lors de la restauration de session (tentative ${retryCount}/${MAX_RETRIES}), nouvelle tentative dans 2 secondes...`);
            }
            setTimeout(() => {
              if (isMounted) {
                restoreSession();
              }
            }, 2000);
          } else {
            // Trop de tentatives, le serveur n'est probablement pas démarré
            // On garde le token mais on arrête les tentatives
            if (process.env.NODE_ENV === 'development') {
              console.warn('Serveur non disponible. Assurez-vous que le serveur backend est démarré sur le port 3001.');
            }
            if (isMounted) {
              dispatch(setLoading(false));
            }
          }
        } else {
          // Autre type d'erreur, retenter si on n'a pas dépassé le maximum
          retryCount++;
          if (retryCount < MAX_RETRIES && isMounted) {
            if (process.env.NODE_ENV === 'development') {
              console.warn(`Erreur réseau lors de la restauration de la session (tentative ${retryCount}/${MAX_RETRIES}), nouvelle tentative dans 2 secondes...`, error);
            }
            setTimeout(() => {
              if (isMounted) {
                restoreSession();
              }
            }, 2000);
          } else if (isMounted) {
            if (process.env.NODE_ENV === 'development') {
              console.error('Impossible de restaurer la session après plusieurs tentatives');
            }
            dispatch(setLoading(false));
          }
        }
      }
    };

    // Restaurer la session au chargement
    restoreSession();

    // Nettoyage lors du démontage du composant
    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  // Ce composant ne rend rien
  return null;
};

export default SessionRestorer;

